import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchHistoryDropdown } from "@/components/SearchHistoryDropdown";
import { SearchHistoryItem } from "@/hooks/useSearchHistory";
import { MobileManufacturerSearch } from "@/components/mobile/MobileManufacturerSearch";
import { cn } from "@/lib/utils";

interface ManufacturerOption {
  label: string;
  value: string;
}

interface MobileSearchBarProps {
  oemNumber: string;
  manufacturerRef: string;
  selectedManufacturer?: string;
  onOemChange: (value: string) => void;
  onRefChange: (value: string) => void;
  onManufacturerChange?: (value: string) => void;
  onOemSearch: (e: React.FormEvent) => void;
  onRefSearch: (e: React.FormEvent) => void;
  isOemSearching: boolean;
  isRefSearching: boolean;
  onFilterClick?: () => void;
  activeFiltersCount?: number;
  disabled?: boolean;
  searchProgress?: number;
  isDemo?: boolean;
  history?: SearchHistoryItem[];
  onSelectHistoryItem?: (query: string, type: "oem" | "ref") => void;
  onRemoveHistoryItem?: (query: string) => void;
  onClearHistory?: () => void;
  isOemHistoryOpen?: boolean;
  isRefHistoryOpen?: boolean;
  onOemFocus?: () => void;
  onOemBlur?: () => void;
  onRefFocus?: () => void;
  onRefBlur?: () => void;
  getHistoryByType?: (type: "oem" | "ref") => SearchHistoryItem[];
  activeTab?: "oem" | "ref";
  onTabChange?: (tab: "oem" | "ref") => void;
  onCancelSearch?: () => void;
  manufacturers?: ManufacturerOption[];
}

const MobileSearchBar = ({ 
  oemNumber,
  manufacturerRef,
  selectedManufacturer = "",
  onOemChange,
  onRefChange,
  onManufacturerChange,
  onOemSearch,
  onRefSearch,
  isOemSearching,
  isRefSearching,
  onFilterClick,
  activeFiltersCount = 0,
  disabled = false,
  searchProgress = 0,
  isDemo = false,
  history = [],
  onSelectHistoryItem,
  onRemoveHistoryItem,
  onClearHistory,
  isOemHistoryOpen = false,
  isRefHistoryOpen = false,
  onOemFocus,
  onOemBlur,
  onRefFocus,
  onRefBlur,
  getHistoryByType,
  activeTab = "oem",
  onTabChange,
  onCancelSearch,
  manufacturers = []
}: MobileSearchBarProps) => {
  return (
    <div className="relative w-full">
      <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-3">
          <TabsTrigger value="oem">OEM</TabsTrigger>
          <TabsTrigger value="ref">Référence</TabsTrigger>
        </TabsList>
        
        {/* OEM TAB */}
        <TabsContent value="oem">
          <form onSubmit={onOemSearch} className="space-y-3">
            <div className="relative">
              {/* Icône fixe de recherche, sans spinner dans le champ */}
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={oemNumber}
                onChange={(e) => onOemChange(e.target.value)}
                onFocus={onOemFocus}
                onBlur={onOemBlur}
                placeholder={isOemSearching ? "Recherche en cours..." : "Ex: 1K0615301"}
                className={cn(
                  "pl-10 h-12 text-base",
                  disabled && "cursor-not-allowed opacity-60",
                  isOemSearching && "text-muted-foreground"
                )}
                disabled={isOemSearching || disabled}
                readOnly={isOemSearching}
              />
              {getHistoryByType && onSelectHistoryItem && onRemoveHistoryItem && onClearHistory && (
                <SearchHistoryDropdown
                  history={getHistoryByType("oem")}
                  onSelectItem={(query) => onSelectHistoryItem(query, "oem")}
                  onRemoveItem={onRemoveHistoryItem}
                  onClearAll={onClearHistory}
                  isVisible={isOemHistoryOpen}
                  searchType="oem"
                />
              )}
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2">
              {isOemSearching ? (
                <Button 
                  type="button"
                  variant="destructive"
                  className="h-12 text-base font-semibold"
                  onClick={onCancelSearch}
                >
                  Annuler
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  className="h-12 text-base font-semibold"
                  disabled={!oemNumber.trim() || disabled}
                >
                  Rechercher OEM
                </Button>
              )}
              
              {onFilterClick && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 relative"
                  onClick={onFilterClick}
                  disabled={isOemSearching || disabled}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  {activeFiltersCount > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs font-bold"
                      variant="destructive"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              )}
            </div>
            
            {/* Barre de progression linéaire */}
            {isOemSearching && !isDemo && (
              <div className="mt-2">
                <Progress value={searchProgress} className="h-1 animate-pulse" />
              </div>
            )}
          </form>
        </TabsContent>
        
        {/* REF TAB */}
        <TabsContent value="ref">
          <form onSubmit={onRefSearch} className="space-y-3">
            {/* Layout vertical: Marque puis Référence */}
            {manufacturers.length > 0 && onManufacturerChange && (
              <>
                {/* Ligne 1: Recherche de marque */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Marque <span className="text-destructive">*</span>
                  </label>
                  <MobileManufacturerSearch
                    manufacturers={manufacturers}
                    value={selectedManufacturer || ""}
                    onChange={onManufacturerChange}
                    disabled={isRefSearching || disabled}
                    className="w-full"
                  />
                </div>

                {/* Ligne 2: Champ référence */}
                <div className="space-y-1.5 relative">
                  <label className="text-xs font-medium text-foreground">
                    Référence <span className="text-destructive">*</span>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                    <Input
                      type="search"
                      value={manufacturerRef}
                      onChange={(e) => onRefChange(e.target.value)}
                      onFocus={onRefFocus}
                      onBlur={onRefBlur}
                      placeholder={selectedManufacturer ? "Ex: LP2357, BG4321" : "Sélectionnez d'abord une marque"}
                      className={cn(
                        "pl-10 h-12 text-base",
                        (!selectedManufacturer || disabled) && "cursor-not-allowed opacity-60",
                        isRefSearching && "text-muted-foreground"
                      )}
                      disabled={!selectedManufacturer || isRefSearching || disabled}
                      readOnly={isRefSearching}
                    />
                  </div>
                  {getHistoryByType && onSelectHistoryItem && onRemoveHistoryItem && onClearHistory && (
                    <SearchHistoryDropdown
                      history={getHistoryByType("ref")}
                      onSelectItem={(query) => onSelectHistoryItem(query, "ref")}
                      onRemoveItem={onRemoveHistoryItem}
                      onClearAll={onClearHistory}
                      isVisible={isRefHistoryOpen}
                      searchType="ref"
                    />
                  )}
                </div>
              </>
            )}

            <div className="grid grid-cols-[1fr_auto] gap-2">
              {isRefSearching ? (
                <Button 
                  type="button"
                  variant="destructive"
                  className="h-12 text-base font-semibold"
                  onClick={onCancelSearch}
                >
                  Annuler
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  className="h-12 text-base font-semibold"
                  disabled={!manufacturerRef.trim() || !selectedManufacturer || disabled}
                >
                  Rechercher Réf
                </Button>
              )}
              
              {onFilterClick && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-12 w-12 relative"
                  onClick={onFilterClick}
                  disabled={isRefSearching || disabled}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  {activeFiltersCount > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs font-bold"
                      variant="destructive"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              )}
            </div>
            
            {/* Barre de progression linéaire */}
            {isRefSearching && !isDemo && (
              <div className="mt-2">
                <Progress value={searchProgress} className="h-1 animate-pulse" />
              </div>
            )}
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MobileSearchBar;
